/* ═══════════════════════════════════════════════════════════════
   ProjectView — projects-as-rows layout for the forecasting grid.

   Layout (keeps the page-level CSS grid: 260px | repeat(7,1fr) | 140px):
     ┌─────────────────────────────────────────────────────────────┐
     │ Project A — proj #001    [day totals × 7]    32h this week  │
     │   ▸ Smith, Jane            [her cells]       12h            │
     │   ▸ Doe, John              [his cells]        8h            │
     │ Project B …                                                 │
     └─────────────────────────────────────────────────────────────┘

   Breakdown options (toolbar):
     - individual: one editable sub-row per person on the project
     - team:        one analytic sub-row per team with members on it
     - department:  one analytic sub-row per dept with members on it

   Cells in the project HEADER row sum across all sub-rows for that day.
   Cells in INDIVIDUAL sub-rows are clickable → opens CellEditor scoped
   to that (member, project, day). Cells in team/dept sub-rows are
   read-only because a click can't disambiguate which member's row to
   write to.

   Scope (Mine / My team / My projects / All) filters which MEMBERS feed
   into the aggregation. So a "My team" PM viewing project breakdown by
   team only sees their team's contribution to each project.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { TeamMember, HourForecast, Project, Department } from '../../../shared/types';
import type { ProjectAssignment, Team, TeamAssignment } from '../../../shared/entities';
import { type Breakdown, projectColor, todayIso, VACATION_COLOR } from './_shared';

// ─── Types ──────────────────────────────────────────────────────────

interface UnitRow {
  /** Stable id within a project: member.id, team.id, or department.id (or
   *  '__nodept' / '__noteam' for the no-membership bucket). */
  id: string;
  label: string;
  hint?: string; // optional secondary text (e.g. "PM" badge for individual)
  color?: string; // visual marker (department/team color, or member avatar bg)
  initials?: string; // for individual rows (avatar)
  /** Forecasts that aggregate into this unit's row, ALREADY filtered to
   *  one project. Allows day-cell sums + week total without re-scanning. */
  forecasts: HourForecast[];
  /** Member id for routing edits — only set for individual rows. */
  memberId?: string;
  /** This member is assigned to the project but has no forecast hours
   *  on it for the visible week. Surfaced as a muted row so a PM
   *  immediately sees "Smith is on this project but I haven't planned
   *  her any hours". Only set in breakdown='individual'. */
  unforecasted?: boolean;
}

interface ProjectGroup {
  project: Project;
  units: UnitRow[];
  /** Sum of all sub-row hours for the week (= sum of all members' hours
   *  on this project across the visible week). */
  weekTotal: number;
  /** Per-day sums — hard-cached so we don't re-iterate forecasts on each
   *  cell render. Keyed by yyyy-mm-dd. */
  dayTotals: Map<string, number>;
}

// ─── Component ──────────────────────────────────────────────────────

export function ProjectView({
  projects,
  members,
  forecasts,
  weekDays,
  breakdown,
  teams,
  teamAssignments,
  projectAssignments,
  departments,
  collapsedGroups,
  onToggleGroup,
  onCellClick,
  canEditCell,
  showWeekend,
}: {
  /** Projects already narrowed by selectedProjectIds toolbar filter. */
  projects: Project[];
  /** Members already narrowed by scope + view-permission. */
  members: TeamMember[];
  /** Forecasts already loaded for the visible week. */
  forecasts: HourForecast[];
  weekDays: string[];
  breakdown: Breakdown;
  teams: Team[];
  teamAssignments: TeamAssignment[];
  /** project_assignments rows — drives the "assigned but unforecasted"
   *  detection in breakdown='individual' mode. */
  projectAssignments: ProjectAssignment[];
  departments: Department[];
  collapsedGroups: Set<string>;
  onToggleGroup: (id: string) => void;
  onCellClick: (memberId: string, dayDate: string, forecastId?: string) => void;
  canEditCell: (memberId: string) => boolean;
  showWeekend: boolean;
}) {
  // ─── Build lookups ───
  const memberById = React.useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const teamById = React.useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);
  const deptById = React.useMemo(() => new Map(departments.map((d) => [d.id, d])), [departments]);
  // member → set of teams (a member can be on multiple teams)
  const teamsByMember = React.useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const a of teamAssignments) {
      let s = m.get(a.teamMemberId);
      if (!s) {
        s = new Set();
        m.set(a.teamMemberId, s);
      }
      s.add(a.teamId);
    }
    return m;
  }, [teamAssignments]);

  // ─── Build per-project groups + unit aggregations ───
  const groups = React.useMemo<ProjectGroup[]>(() => {
    const out: ProjectGroup[] = [];
    const visibleMemberIds = new Set(members.map((m) => m.id));
    const weekStart = weekDays[0];
    const weekEnd = weekDays[6];

    // Index forecasts by project for O(F) bucketing
    const fcByProject = new Map<string, HourForecast[]>();
    for (const f of forecasts) {
      if (!f.projectId) continue;
      if (f.sourceKind === 'vacation') continue; // vacation handled outside project view
      if (!visibleMemberIds.has(f.teamMemberId)) continue;
      const day = f.dayDate || f.weekStartDate;
      if (!day || day < weekStart || day > weekEnd) continue;
      const arr = fcByProject.get(f.projectId) || [];
      arr.push(f);
      fcByProject.set(f.projectId, arr);
    }

    // Build the assigned-set per project: project_assignments rows ∪
    // PM/PIC links. Used to surface "assigned but no hours forecasted"
    // members under each project — the visible feedback loop a PM
    // wants ("Smith is on this project but I haven't planned anything
    // for her this week").
    const assignedByProject = new Map<string, Set<string>>();
    const ensureAssign = (projId: string) => {
      let s = assignedByProject.get(projId);
      if (!s) {
        s = new Set();
        assignedByProject.set(projId, s);
      }
      return s;
    };
    for (const a of projectAssignments) {
      if (!visibleMemberIds.has(a.teamMemberId)) continue;
      ensureAssign(a.projectId).add(a.teamMemberId);
    }
    for (const p of projects) {
      if (p.pmId && visibleMemberIds.has(p.pmId)) ensureAssign(p.id).add(p.pmId);
      if (p.picId && visibleMemberIds.has(p.picId)) ensureAssign(p.id).add(p.picId);
    }

    for (const project of projects) {
      const projForecasts = fcByProject.get(project.id) || [];
      const assigned = assignedByProject.get(project.id) || new Set<string>();
      // Skip projects with neither forecasts nor assignees — irrelevant.
      // Projects with assignees but no forecasts DO show: every assignee
      // becomes an unforecasted muted row so the gap is unmissable.
      if (projForecasts.length === 0 && assigned.size === 0) continue;

      // Aggregate by chosen breakdown unit.
      const byUnit = new Map<
        string,
        { unit: Omit<UnitRow, 'forecasts'>; forecasts: HourForecast[] }
      >();

      const ensure = (unit: Omit<UnitRow, 'forecasts'>) => {
        let bucket = byUnit.get(unit.id);
        if (!bucket) {
          bucket = { unit, forecasts: [] };
          byUnit.set(unit.id, bucket);
        }
        return bucket;
      };

      for (const f of projForecasts) {
        const member = memberById.get(f.teamMemberId);
        if (!member) continue;

        if (breakdown === 'individual') {
          const initials = member.name
            .split(/\s+/)
            .map((p) => p[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();
          const isPm = project.pmId === member.id;
          const isPic = project.picId === member.id;
          ensure({
            id: member.id,
            label: member.name,
            hint: isPic ? 'PIC' : isPm ? 'PM' : undefined,
            color: 'var(--ac)',
            initials,
            memberId: member.id,
          }).forecasts.push(f);
        } else if (breakdown === 'team') {
          const myTeams = teamsByMember.get(member.id);
          if (!myTeams || myTeams.size === 0) {
            ensure({ id: '__noteam', label: 'No team', color: 'var(--bd)' }).forecasts.push(f);
          } else {
            // A member on multiple teams contributes once per team. Bucket
            // each team's row separately so the totals reflect that the
            // hours belong (operationally) to every team they're part of.
            for (const tid of myTeams) {
              const t = teamById.get(tid);
              if (!t) continue;
              ensure({
                id: t.id,
                label: t.name,
                hint: t.shortCode || undefined,
                color: t.color || 'var(--ac)',
              }).forecasts.push(f);
            }
          }
        } else {
          // department
          if (!member.departmentId) {
            ensure({
              id: '__nodept',
              label: 'No department',
              color: 'var(--bd)',
            }).forecasts.push(f);
          } else {
            const d = deptById.get(member.departmentId);
            ensure({
              id: member.departmentId,
              label: d?.name || 'Unknown department',
              hint: d?.abbreviation,
              color: d?.color || 'var(--ac)',
            }).forecasts.push(f);
          }
        }
      }

      // Inject "assigned but unforecasted" muted rows. Only meaningful in
      // breakdown='individual' — analytic team/dept views don't drill to
      // individual gaps. Member is unforecasted if they're assigned (or
      // PM/PIC) but contributed zero hours to projForecasts this week.
      if (breakdown === 'individual') {
        const forecastedMemberIds = new Set<string>();
        for (const f of projForecasts) forecastedMemberIds.add(f.teamMemberId);
        for (const memberId of assigned) {
          if (forecastedMemberIds.has(memberId)) continue;
          const member = memberById.get(memberId);
          if (!member) continue;
          const initials = member.name
            .split(/\s+/)
            .map((p) => p[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();
          const isPm = project.pmId === member.id;
          const isPic = project.picId === member.id;
          ensure({
            id: member.id,
            label: member.name,
            hint: isPic ? 'PIC' : isPm ? 'PM' : undefined,
            color: 'var(--ac)',
            initials,
            memberId: member.id,
            unforecasted: true,
          });
        }
      }

      // Project-level totals
      const dayTotals = new Map<string, number>();
      let weekTotal = 0;
      for (const f of projForecasts) {
        const day = f.dayDate || f.weekStartDate;
        if (!day) continue;
        const h = Number(f.hours) || 0;
        weekTotal += h;
        dayTotals.set(day, (dayTotals.get(day) || 0) + h);
      }

      // Sort units: forecasted (heaviest first), then unforecasted alpha.
      // Putting the gap below the action is intentional — the user reads
      // top-down from "what we have" to "what's missing".
      const units: UnitRow[] = Array.from(byUnit.values())
        .map(({ unit, forecasts: fs }) => ({ ...unit, forecasts: fs }))
        .sort((a, b) => {
          if (!!a.unforecasted !== !!b.unforecasted) return a.unforecasted ? 1 : -1;
          if (a.unforecasted) return a.label.localeCompare(b.label);
          return unitTotal(b) - unitTotal(a);
        });

      out.push({ project, units, weekTotal, dayTotals });
    }

    // Project order = projects array order (stable, matches user's mental model)
    return out;
  }, [
    projects,
    members,
    forecasts,
    weekDays,
    breakdown,
    memberById,
    teamById,
    deptById,
    teamsByMember,
    projectAssignments,
  ]);

  if (groups.length === 0) {
    return (
      <div style={{ gridColumn: '1 / -1', padding: 40, textAlign: 'center', color: 'var(--tm)' }}>
        No projects with hours forecasted this week.
      </div>
    );
  }

  return (
    <>
      {groups.map((g) => {
        const collapsed = collapsedGroups.has(`proj:${g.project.id}`);
        return (
          <React.Fragment key={g.project.id}>
            <ProjectHeaderRow
              project={g.project}
              units={g.units}
              weekDays={weekDays}
              dayTotals={g.dayTotals}
              weekTotal={g.weekTotal}
              collapsed={collapsed}
              showWeekend={showWeekend}
              onToggle={() => onToggleGroup(`proj:${g.project.id}`)}
            />
            {!collapsed &&
              g.units.map((u) => (
                <UnitSubRow
                  key={u.id}
                  unit={u}
                  project={g.project}
                  weekDays={weekDays}
                  breakdown={breakdown}
                  showWeekend={showWeekend}
                  canEdit={breakdown === 'individual' && !!u.memberId && canEditCell(u.memberId)}
                  onCellClick={
                    breakdown === 'individual' && u.memberId
                      ? (day, fid) => onCellClick(u.memberId!, day, fid)
                      : undefined
                  }
                />
              ))}
          </React.Fragment>
        );
      })}
    </>
  );
}

function unitTotal(u: UnitRow): number {
  let s = 0;
  for (const f of u.forecasts) s += Number(f.hours) || 0;
  return s;
}

// ─── Project header row ─────────────────────────────────────────────

function ProjectHeaderRow({
  project,
  units,
  weekDays,
  dayTotals,
  weekTotal,
  collapsed,
  showWeekend,
  onToggle,
}: {
  project: Project;
  units: UnitRow[];
  weekDays: string[];
  dayTotals: Map<string, number>;
  weekTotal: number;
  collapsed: boolean;
  showWeekend: boolean;
  onToggle: () => void;
}) {
  const color = projectColor(project.id);
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <>
      {/* Left col: project name + colored swatch + toggle */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        aria-label={`${project.name}, ${units.length} contributors, ${weekTotal.toFixed(1)} hours this week, ${collapsed ? 'collapsed' : 'expanded'}`}
        onClick={onToggle}
        onKeyDown={handleKey}
        style={{
          padding: '10px 14px',
          borderTop: '1px solid var(--bd)',
          borderBottom: '1px solid var(--bd)',
          background: 'var(--sa)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          cursor: 'pointer',
          position: 'sticky',
          left: 0,
          zIndex: 1,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 10,
            fontSize: 10,
            color: 'var(--tm)',
            transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
            transition: 'transform .12s',
          }}
        >
          ▶
        </span>
        <span
          aria-hidden="true"
          style={{
            width: 12,
            height: 12,
            borderRadius: 3,
            background: color,
            flexShrink: 0,
          }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--tx)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={project.name}
          >
            {project.name}
          </div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--tm)',
              fontFamily: "'JetBrains Mono', monospace",
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <span>
              {project.projectNumber || '—'} · {units.length} contributor
              {units.length === 1 ? '' : 's'}
            </span>
            {(() => {
              const unforecastedCount = units.filter((u) => u.unforecasted).length;
              if (unforecastedCount === 0) return null;
              return (
                <span
                  title={`${unforecastedCount} ${unforecastedCount === 1 ? 'person is' : 'people are'} assigned but have no forecast hours this week`}
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: 999,
                    background: 'color-mix(in srgb, #ea580c 14%, transparent)',
                    color: '#ea580c',
                    border: '1px solid color-mix(in srgb, #ea580c 40%, transparent)',
                    letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    fontFamily: 'inherit',
                  }}
                >
                  ⚠ {unforecastedCount} unforecasted
                </span>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Day cells: project-total hours per day */}
      {weekDays.map((day) => {
        const total = dayTotals.get(day) || 0;
        const [y, m, d] = day.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        const isWeekend = dt.getUTCDay() === 0 || dt.getUTCDay() === 6;
        const isToday = day === todayIso();
        return (
          <div
            key={day}
            style={{
              padding: '10px 6px',
              borderTop: '1px solid var(--bd)',
              borderBottom: '1px solid var(--bd)',
              borderLeft: '1px solid var(--bd)',
              background: isToday
                ? 'color-mix(in srgb, var(--ac) 6%, var(--sa))'
                : isWeekend && !showWeekend
                  ? 'color-mix(in srgb, var(--bd) 30%, var(--sa))'
                  : 'var(--sa)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              color: total > 0 ? color : 'var(--tm)',
              fontVariantNumeric: 'tabular-nums',
              opacity: isWeekend && !showWeekend ? 0.55 : 1,
            }}
          >
            {total > 0 ? `${total}h` : '—'}
          </div>
        );
      })}

      {/* Right col: weekly total */}
      <div
        style={{
          padding: '10px 12px',
          borderTop: '1px solid var(--bd)',
          borderBottom: '1px solid var(--bd)',
          borderLeft: '1px solid var(--bd)',
          background: 'var(--sa)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--tx)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {weekTotal.toFixed(1)}h
        </span>
        <span
          style={{
            fontSize: 9,
            color: 'var(--tm)',
            textTransform: 'uppercase',
            letterSpacing: '.04em',
          }}
        >
          this week
        </span>
      </div>
    </>
  );
}

// ─── Per-unit sub-row ───────────────────────────────────────────────

function UnitSubRow({
  unit,
  project,
  weekDays,
  breakdown,
  showWeekend,
  canEdit,
  onCellClick,
}: {
  unit: UnitRow;
  project: Project;
  weekDays: string[];
  breakdown: Breakdown;
  showWeekend: boolean;
  canEdit: boolean;
  onCellClick?: (day: string, forecastId?: string) => void;
}) {
  // Index this unit's forecasts by day for O(1) cell lookups.
  const fcByDay = React.useMemo(() => {
    const m = new Map<string, HourForecast[]>();
    for (const f of unit.forecasts) {
      const d = f.dayDate || f.weekStartDate;
      if (!d) continue;
      const arr = m.get(d) || [];
      arr.push(f);
      m.set(d, arr);
    }
    return m;
  }, [unit.forecasts]);

  const projColor = projectColor(project.id);
  const weekTotal = unitTotal(unit);

  // Unforecasted rows are visually muted so they read as a gap/warning,
  // not as content. Keep the avatar legible (full color) so the user can
  // still identify the person quickly.
  const unforecasted = !!unit.unforecasted;

  return (
    <>
      {/* Left col: unit identity */}
      <div
        style={{
          padding: '8px 14px 8px 36px', // indent under project header arrow
          borderBottom: '1px solid var(--bd)',
          background: unforecasted ? 'color-mix(in srgb, #ea580c 4%, var(--sf))' : 'var(--sf)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          position: 'sticky',
          left: 0,
          zIndex: 1,
        }}
      >
        {/* Identity badge: avatar (individual), color square (team/dept) */}
        {breakdown === 'individual' && unit.initials ? (
          <div
            aria-hidden="true"
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: unit.color || 'var(--ac)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              flexShrink: 0,
              opacity: unforecasted ? 0.55 : 1,
            }}
          >
            {unit.initials}
          </div>
        ) : (
          <span
            aria-hidden="true"
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: unit.color || 'var(--bd)',
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: unforecasted ? 'var(--tm)' : 'var(--tx)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={
              unforecasted
                ? `${unit.label} is assigned to ${project.name} but has no forecast hours this week — click any day cell to plan some.`
                : unit.label
            }
          >
            {unit.label}
            {unforecasted && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '.04em',
                  textTransform: 'uppercase',
                  color: '#ea580c',
                  background: 'color-mix(in srgb, #ea580c 14%, transparent)',
                  padding: '1px 5px',
                  borderRadius: 3,
                }}
              >
                Unforecasted
              </span>
            )}
            {unit.hint && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '.04em',
                  textTransform: 'uppercase',
                  color: 'var(--ac)',
                  background: 'color-mix(in srgb, var(--ac) 14%, transparent)',
                  padding: '1px 5px',
                  borderRadius: 3,
                }}
              >
                {unit.hint}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Day cells */}
      {weekDays.map((day) => {
        const dayForecasts = fcByDay.get(day) || [];
        const dayTotal = dayForecasts.reduce((s, f) => s + (Number(f.hours) || 0), 0);
        const [y, m, d] = day.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        const isWeekend = dt.getUTCDay() === 0 || dt.getUTCDay() === 6;
        const isToday = day === todayIso();

        // For individual breakdown with exactly one forecast row, allow
        // routing the click to that forecast's id (so editor opens that
        // exact row). With multiple rows we just use empty cell + day.
        const singleForecastId =
          breakdown === 'individual' && dayForecasts.length === 1 ? dayForecasts[0].id : undefined;
        const clickable = breakdown === 'individual' && canEdit && !!onCellClick;

        const handle = clickable ? () => onCellClick!(day, singleForecastId) : undefined;
        const handleKey = clickable
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCellClick!(day, singleForecastId);
              }
            }
          : undefined;

        return (
          <div
            key={day}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={
              clickable ? `${day} — ${dayTotal}h. Click to edit.` : `${day} — ${dayTotal}h.`
            }
            onClick={handle}
            onKeyDown={handleKey}
            style={{
              padding: 4,
              borderBottom: '1px solid var(--bd)',
              borderLeft: '1px solid var(--bd)',
              background: isToday
                ? 'color-mix(in srgb, var(--ac) 4%, transparent)'
                : isWeekend && !showWeekend
                  ? 'color-mix(in srgb, var(--bd) 18%, transparent)'
                  : '',
              cursor: clickable ? 'pointer' : 'default',
              minHeight: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isWeekend && !showWeekend ? 0.55 : 1,
            }}
            title={
              breakdown === 'individual'
                ? clickable
                  ? dayTotal > 0
                    ? `${dayTotal}h on ${project.name} — click to edit`
                    : 'Click to assign hours'
                  : 'Read-only'
                : `${dayTotal}h on ${project.name} (analytic view — switch to Person to edit)`
            }
          >
            {dayTotal > 0 ? (
              <div
                style={{
                  background: `color-mix(in srgb, ${projColor} 18%, transparent)`,
                  border: `1px solid ${projColor}`,
                  borderLeft: `3px solid ${projColor}`,
                  borderRadius: 4,
                  padding: '3px 8px',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--tx)',
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: 36,
                  textAlign: 'center',
                }}
              >
                {dayTotal}h
              </div>
            ) : (
              <span style={{ color: 'var(--tm)', fontSize: 10 }}>—</span>
            )}
          </div>
        );
      })}

      {/* Right col: unit's weekly total on this project */}
      <div
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid var(--bd)',
          borderLeft: '1px solid var(--bd)',
          background: unforecasted ? 'color-mix(in srgb, #ea580c 4%, var(--sf))' : 'var(--sf)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: weekTotal > 0 ? 'var(--tx)' : 'var(--tm)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {weekTotal.toFixed(1)}h
        </span>
      </div>
    </>
  );
}

// Suppress lint warning for VACATION_COLOR import — kept for potential
// future extension (vacation-flag overlay on project totals).
void VACATION_COLOR;
